import ServiceTemplate from '../../../templates/service/ServiceTemplate';
import type { ServiceConfig } from '../../../templates/service/types';

const config: ServiceConfig = {
  pageTitle: 'Commercial Electrician Sleaford | Commercial Electrical Services Lincolnshire | Quantum Electrical',
  metaDescription: 'Commercial electrical services in Sleaford and Lincolnshire — installations, three-phase power, fit-outs, EV charging, lighting and compliance. NAPIT registered. Call 01529 265060.',
  keywords: 'commercial electrician Sleaford, commercial electrical Sleaford, commercial electrical installation Lincolnshire, industrial electrician Sleaford, office electrical Sleaford, three phase power Sleaford',
  canonicalPath: '/services/commercial',

  heroBadge: 'NAPIT Registered · Sleaford & Lincolnshire',
  heroTitle: 'Commercial Electrical',
  heroHighlight: 'Services',
  heroLocation: 'Sleaford, Lincolnshire',
  heroDesc: 'Quantum Electrical delivers professional commercial electrical services across Sleaford and Lincolnshire — from full office fit-outs and industrial three-phase installations to commercial EV charging, LED upgrades and full compliance documentation. NAPIT registered, experienced in commercial environments, and trusted by businesses throughout the region.',
  heroImageUrl: 'https://public.readdy.ai/ai/img_res/1a71ca6bd0f9317a3377a5c118a03029.jpg',

  servicesHeading: 'Commercial Electrical Services We Offer',
  servicesSubheading: 'Complete commercial electrical services for offices, retail, industrial premises and businesses throughout Sleaford and Lincolnshire.',
  services: [
    { icon: 'ri-building-fill', title: 'Commercial Installations', desc: 'New electrical installations for offices, retail units and commercial premises in Sleaford — fully certified.' },
    { icon: 'ri-equalizer-fill', title: 'Three Phase Power', desc: 'Three-phase supply installations and distribution boards for heavy load commercial premises in Lincolnshire.' },
    { icon: 'ri-flashlight-fill', title: 'Commercial Rewiring', desc: 'Full commercial rewiring carried out with minimal disruption to Sleaford businesses — certified and documented.' },
    { icon: 'ri-briefcase-fill', title: 'Office Electrical Fit-Outs', desc: 'Complete office electrical fit-outs from first fix to final finish — data, sockets, lighting and distribution.' },
    { icon: 'ri-store-fill', title: 'Retail Electrical Installations', desc: 'Shop electrical fit-outs, display lighting, till point power and signage circuits throughout Lincolnshire.' },
    { icon: 'ri-community-fill', title: 'Industrial Electrical', desc: 'Industrial machinery circuits, heavy current wiring and factory electrical work across Lincolnshire.' },
    { icon: 'ri-wifi-fill', title: 'Data & IT Cabling', desc: 'Structured data cabling, server room power and IT infrastructure for Sleaford commercial premises.' },
    { icon: 'ri-lightbulb-flash-fill', title: 'Commercial Lighting', desc: 'LED upgrades, emergency lighting and bespoke feature lighting for commercial premises in Sleaford.' },
    { icon: 'ri-car-fill', title: 'Commercial EV Charging', desc: 'Workplace and commercial car park EV charging installations for Sleaford businesses — OZEV approved.' },
    { icon: 'ri-building-2-fill', title: 'Warehouse & Industrial Fit-Out', desc: 'High-bay lighting, power distribution and complete electrical fit-outs for warehouse premises in Lincolnshire.' },
    { icon: 'ri-calendar-fill', title: 'Commercial Maintenance', desc: 'Planned maintenance contracts for Sleaford businesses — scheduled visits, full documentation.' },
    { icon: 'ri-file-certificate-fill', title: 'Commercial EICR & Compliance', desc: 'Full EICR reports, PAT testing and compliance documentation for commercial premises across Lincolnshire.' },
  ],

  whyUs: [
    {
      icon: 'ri-building-fill',
      title: 'Commercial Experience',
      desc: 'We understand the demands of commercial environments — minimal disruption, on-time delivery and clean workmanship.',
    },
    {
      icon: 'ri-shield-check-fill',
      title: 'NAPIT Registered',
      desc: 'All commercial electrical work in Sleaford is carried out by NAPIT registered, fully qualified electricians.',
    },
    {
      icon: 'ri-file-certificate-fill',
      title: 'Full Documentation',
      desc: 'Electrical Installation Certificates, EICR reports and all compliance documentation issued on completion.',
    },
    {
      icon: 'ri-time-fill',
      title: 'Minimal Disruption',
      desc: 'We work around your business — out of hours, weekends and phased programmes available for Sleaford businesses.',
    },
  ],

  processSteps: [
    {
      number: '01',
      icon: 'ri-phone-fill',
      title: 'Consultation',
      desc: 'Call or email to discuss your commercial electrical project — we\'ll advise and arrange a free site visit in Sleaford.',
    },
    {
      number: '02',
      icon: 'ri-map-pin-2-fill',
      title: 'Site Survey',
      desc: 'We survey your Sleaford premises, assess requirements and provide a detailed, fixed-price written quotation.',
    },
    {
      number: '03',
      icon: 'ri-tools-fill',
      title: 'Installation',
      desc: 'Work carried out professionally by our commercial team — on programme, on budget, minimal disruption.',
    },
    {
      number: '04',
      icon: 'ri-file-certificate-fill',
      title: 'Sign-Off',
      desc: 'Full certification, test results and all compliance documentation issued — your Sleaford business is covered.',
    },
  ],

  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=commercial%20office%20electrical%20installation%20trunking%20cable%20management%20sockets%20data%20points%20UK%20business%20interior%20professional%20neat%20no%20people%20Lincolnshire%20Sleaford%20dark%20background%20amber%20warm%20glow%20fit-out&width=800&height=600&seq=gal-comm-01-v1&orientation=landscape',
      alt: 'Commercial office electrical installation Sleaford',
      caption: 'Office Fit-Out — Sleaford',
      label: 'Office Install',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=three%20phase%20commercial%20distribution%20board%20electrical%20panel%20UK%20industrial%20premises%20no%20people%20amber%20dark%20Lincolnshire%20Sleaford%20heavy%20duty%20electrical%20installation%20three%20phase%20power%20professional%20close%20up&width=600&height=500&seq=gal-comm-02-v1&orientation=squarish',
      alt: 'Three phase power installation Sleaford',
      caption: 'Three Phase Distribution — Sleaford',
      label: 'Three Phase',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=commercial%20LED%20lighting%20installation%20UK%20office%20retail%20premises%20interior%20bright%20professional%20lighting%20no%20people%20Lincolnshire%20Sleaford%20modern%20business%20lighting%20upgrade%20energy%20saving&width=600&height=500&seq=gal-comm-03-v1&orientation=squarish',
      alt: 'Commercial LED lighting Sleaford',
      caption: 'Commercial LED Upgrade — Lincolnshire',
      label: 'LED Lighting',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=commercial%20EV%20charger%20charging%20point%20UK%20business%20car%20park%20workplace%20installation%20no%20people%20Lincolnshire%20Sleaford%20professional%20outdoor%20EV%20charging%20station%20modern&width=600&height=500&seq=gal-comm-04-v1&orientation=squarish',
      alt: 'Commercial EV charging Sleaford',
      caption: 'Workplace EV Charging — Sleaford',
      label: 'EV Charging',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=warehouse%20high%20bay%20LED%20lighting%20UK%20industrial%20premises%20interior%20professional%20installation%20no%20people%20Lincolnshire%20Sleaford%20industrial%20electrical%20fit-out%20warehouse%20lighting%20bright&width=600&height=500&seq=gal-comm-05-v1&orientation=squarish',
      alt: 'Warehouse lighting Sleaford',
      caption: 'Warehouse Lighting — Lincolnshire',
      label: 'Warehouse',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=retail%20shop%20electrical%20installation%20UK%20store%20interior%20display%20lighting%20sockets%20neat%20professional%20no%20people%20Lincolnshire%20Sleaford%20commercial%20retail%20electrical%20fit-out&width=600&height=500&seq=gal-comm-06-v1&orientation=squarish',
      alt: 'Retail electrical installation Sleaford',
      caption: 'Retail Fit-Out — Sleaford',
      label: 'Retail',
    },
  ],

  faqHeading: 'Commercial Electrical Questions',
  faqSubheading: 'Common questions about commercial electrical services for businesses in Sleaford and across Lincolnshire.',
  faqs: [
    {
      q: 'What commercial electrical services do you offer in Sleaford?',
      a: 'We offer a full range of commercial electrical services in Sleaford and Lincolnshire — including new installations, three-phase power, commercial rewiring, office and retail fit-outs, industrial electrical work, data cabling, commercial LED lighting, workplace EV charging, and full compliance documentation including EICR reports and PAT testing. One team, one invoice.',
    },
    {
      q: 'Can you work around our business hours in Sleaford?',
      a: 'Absolutely — we understand that electrical works can be disruptive to a trading Sleaford business. We offer early morning starts, evening and weekend attendance, and can plan phased programmes of work to minimise impact on your staff, customers and operations. Just let us know your requirements when enquiring.',
    },
    {
      q: 'Do you carry out three-phase electrical installations in Sleaford?',
      a: 'Yes — we carry out three-phase supply installations, distribution boards and heavy current wiring for commercial and industrial premises throughout Sleaford and Lincolnshire. Whether you need a new three-phase supply brought in, a distribution board installed, or additional three-phase circuits for machinery, our commercial team can handle the full project.',
    },
    {
      q: 'Do you carry out commercial office fit-outs in Sleaford?',
      a: 'Yes — we carry out complete commercial electrical fit-outs from first fix through to final test and sign-off. This includes distribution boards, wiring, socket and switch installation, data point provision, lighting circuits, emergency lighting and full testing and certification. We work with developers, contractors and direct clients across Sleaford and Lincolnshire.',
    },
    {
      q: 'Can you install EV chargers for a Sleaford business or car park?',
      a: 'Yes — we are OZEV authorised for commercial EV charger installations. We install individual workplace charge points, multi-unit car park charging solutions and load-managed systems for Sleaford businesses. We advise on the most appropriate solution for your premises, available grants and infrastructure requirements.',
    },
    {
      q: 'What commercial EICR and compliance services do you provide in Sleaford?',
      a: 'Our commercial compliance service covers: EICR (Electrical Installation Condition Report) inspections and remedials, PAT testing, fire alarm testing and certification, emergency lighting inspection and certification, and ongoing maintenance contracts. We issue all required documentation for your Sleaford business records and any applicable licences or insurance requirements.',
    },
    {
      q: 'Do you offer planned maintenance contracts for Sleaford businesses?',
      a: 'Yes — we offer planned preventive maintenance contracts for commercial premises in Sleaford. These cover scheduled EICR inspections, PAT testing, emergency lighting testing, minor repairs and general electrical maintenance — all for a fixed annual fee. This gives you complete peace of mind and ensures your electrical compliance never lapses.',
    },
    {
      q: 'How much does commercial electrical installation cost in Sleaford?',
      a: 'Commercial electrical pricing in Sleaford depends on the scale and complexity of the project. We always provide a fixed, detailed written quotation before commencing any work. For smaller commercial jobs such as additional sockets or lighting circuits, pricing is competitive with domestic work. For larger fit-outs and industrial projects, we provide a full breakdown by trade. Call 01529 265060 to discuss your project.',
    },
  ],

  mapHeading: 'Commercial Electrical Services Across Sleaford & Lincolnshire',
  mapSubheading: 'Professional commercial electrical services for businesses, offices, retail and industrial premises throughout Sleaford and all of Lincolnshire.',
  mapEmbedSrc: 'https://maps.google.com/maps?q=Sleaford,Lincolnshire,UK&t=&z=12&ie=UTF8&iwloc=&output=embed',
  mapAreaList: ['Sleaford', 'Grantham', 'Boston', 'Spalding', 'Lincoln', 'Newark-on-Trent', 'Stamford', 'Bourne', 'Heckington', 'Ruskington', 'Ancaster', 'Cranwell', 'Leadenham', 'Navenby', 'Metheringham', 'Kirkby la Thorpe'],

  formUrl: 'https://readdy.ai/api/form/d73surucsbv12mkucrig',
};

export default function CommercialPage() {
  return <ServiceTemplate config={config} />;
}
